// src/pages/Problems/ProblemDetailPage.js
import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Paper, Box, Chip, Button, Grid, Divider,
  Avatar, TextField, Card, CardContent, CircularProgress, Alert,
  IconButton, Collapse, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, Autocomplete, Tooltip, Slide,
} from '@mui/material';
import {
  Edit, ArrowBack, Send, Lightbulb, Close, ExpandMore, ExpandLess,
  Business, Visibility as VisibilityIcon, Update, Payments, Event,
  Gavel, AutoAwesome, Info, CheckCircle, LocalOffer, Delete,
  ContactMail, OpenInNew, ErrorOutline,ChatBubbleOutline,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAppSelector } from '../../store/hooks';
import { formatDistance } from 'date-fns';
import SolutionSuggestions from '../../components/ai/SolutionSuggestions';
import PricePrediction from '../../components/ai/PricePrediction';

const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softBg: '#F8FBF8',
  softGreen: 'rgba(46, 125, 50, 0.06)',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
};

const SlideTransition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProblemDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);

  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [suggestionsExpanded, setSuggestionsExpanded] = useState(true);

  // Tagged solutions
  const [taggedSolutions, setTaggedSolutions] = useState([]);
  const [loadingTagged, setLoadingTagged] = useState(false);

  // Tag dialog
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [mySolutions, setMySolutions] = useState([]);
  const [loadingSolutions, setLoadingSolutions] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState(null);
  const [tagNote, setTagNote] = useState('');
  const [tagging, setTagging] = useState(false);
  const [tagSuccess, setTagSuccess] = useState(null);
  
  // Already tagged dialog
  const [alreadyTaggedDialogOpen, setAlreadyTaggedDialogOpen] = useState(false);
  const [alreadyTaggedSolution, setAlreadyTaggedSolution] = useState(null);
  const [alreadyTaggedDialogTimer, setAlreadyTaggedDialogTimer] = useState(null);

  // Bid states
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [bidData, setBidData] = useState({ title: '', proposal: '', proposed_amount: '', estimated_timeline: '' });
  const [submittingBid, setSubmittingBid] = useState(false);
  const [existingBid, setExistingBid] = useState(null);
  const [bidsList, setBidsList] = useState([]);
  const [showBidsList, setShowBidsList] = useState(false);
  const [generatingBid, setGeneratingBid] = useState(false);
  const [aiBidProposal, setAiBidProposal] = useState(null);
  const [aiBidDialogOpen, setAiBidDialogOpen] = useState(false);

  // Auto-close for already tagged dialog
  useEffect(() => {
    if (alreadyTaggedDialogOpen) {
      const timer = setTimeout(() => {
        setAlreadyTaggedDialogOpen(false);
      }, 4000);
      setAlreadyTaggedDialogTimer(timer);
      
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [alreadyTaggedDialogOpen]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/problems/${id}` } });
    }
  }, [isAuthenticated, navigate, id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    fetchProblem();
    fetchTaggedSolutions();
    if (user?.user_type === 'supplier') checkExistingBid();
    if (user?.user_type === 'organization') fetchBids();
  }, [id, isAuthenticated, user?.user_type]);

  const fetchProblem = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/problems/${id}/`);
      setProblem(data);
    } catch (err) {
      console.error('Error fetching problem:', err);
      if (err.status === 401) {
        navigate('/login', { state: { from: `/problems/${id}` } });
      } else {
        setError(err.message || 'Failed to load problem');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTaggedSolutions = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoadingTagged(true);
      const data = await api.get(`/problems/${id}/tagged-solutions/`);
      setTaggedSolutions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching tagged solutions:', err);
      setTaggedSolutions([]);
    } finally {
      setLoadingTagged(false);
    }
  };

  const fetchMySolutions = async () => {
    if (!isAuthenticated || user?.user_type !== 'supplier') return;
    
    try {
      setLoadingSolutions(true);
      
      const orgData = await api.get('/organizations/my-organizations/');
      const orgs = Array.isArray(orgData)
        ? orgData
        : orgData?.results || (orgData?.id ? [orgData] : []);
      
      if (orgs.length === 0) {
        setMySolutions([]);
        return;
      }
      
      const results = await Promise.all(
        orgs.map((org) =>
          api.solutions.list({ provider: org.id, page: 1, page_size: 100 }).catch(() => ({ results: [] }))
        )
      );
      
      const allSolutions = results.flatMap((res) => res?.results || []);
      
      const seen = new Set();
      const unique = allSolutions.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
      
      setMySolutions(unique);
    } catch (err) {
      console.error('Error fetching own solutions:', err);
      setMySolutions([]);
    } finally {
      setLoadingSolutions(false);
    }
  };

  const openTagDialog = () => {
    setSelectedSolution(null);
    setTagNote('');
    setTagSuccess(null);
    setTagDialogOpen(true);
    fetchMySolutions();
  };

  const handleTagSolution = async () => {
    if (!selectedSolution) return;
    
    // Check if solution is already tagged to this problem
    const isAlreadyTagged = taggedSolutions.some(tag => tag.solution_id === selectedSolution.id);
    if (isAlreadyTagged) {
      setAlreadyTaggedSolution(selectedSolution);
      setTagDialogOpen(false);
      setAlreadyTaggedDialogOpen(true);
      return;
    }
    
    try {
      setTagging(true);
      await api.post(`/problems/${id}/tag-solution/`, {
        solution_id: selectedSolution.id,
        note: tagNote,
      });
      setTagSuccess(selectedSolution.title);
      fetchTaggedSolutions();
      if (problem) {
        setProblem(prev => ({ ...prev, solutions_count: (prev.solutions_count || 0) + 1 }));
      }
    } catch (err) {
      console.error('Error tagging solution:', err);
      const errorData = err.response?.data || err.data || {};
      alert(errorData.error || 'Failed to tag solution');
    } finally {
      setTagging(false);
    }
  };

  const handleUntagSolution = async (solutionId) => {
    if (!window.confirm('Remove this solution tag?')) return;
    
    try {
      await api.delete(`/problems/${id}/untag-solution/${solutionId}/`);
      fetchTaggedSolutions();
      if (problem) {
        setProblem(prev => ({ ...prev, solutions_count: Math.max(0, (prev.solutions_count || 1) - 1) }));
      }
    } catch (err) {
      console.error('Error untagging solution:', err);
      const errorData = err.response?.data || err.data || {};
      alert(errorData.error || 'Failed to untag solution');
    }
  };

  const checkExistingBid = async () => {
    if (user?.user_type !== 'supplier') return;
    
    try {
      const data = await api.get(`/problems/${id}/bids/`);
      const userBid = data.find(bid => bid.supplier === user?.id);
      if (userBid) setExistingBid(userBid);
    } catch (err) {
      console.log('Unable to check existing bids');
    }
  };

  const fetchBids = async () => {
    if (user?.user_type !== 'organization') return;
    
    try {
      const data = await api.get(`/problems/${id}/bids/`);
      setBidsList(data);
    } catch (err) {
      console.log('Error fetching bids:', err);
    }
  };

  const handleAddUpdate = async () => {
    if (!comment.trim()) return;
    
    try {
      setSubmitting(true);
      await api.post(`/problems/${id}/add_update/`, { content: comment });
      setComment('');
      fetchProblem();
    } catch (err) {
      console.error('Error adding update:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBidSubmit = async () => {
    if (!bidData.title || !bidData.proposal) { 
      alert('Please fill in all required fields'); 
      return; 
    }
    
    try {
      setSubmittingBid(true);
      await api.post(`/problems/${id}/submit_bid/`, bidData);
      setBidDialogOpen(false);
      setBidData({ title: '', proposal: '', proposed_amount: '', estimated_timeline: '' });
      alert('Bid submitted successfully!');
      checkExistingBid();
      fetchBids();
    } catch (err) {
      const errorData = err.response?.data || err.data || {};
      alert(errorData.error || 'Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const generateAIBid = async () => {
    try {
      setGeneratingBid(true);
      const response = await api.post(`/ai/generate-bid/${id}/`, { auto_submit: false });
      if (response.proposal) {
        setAiBidProposal(response.proposal);
        setBidData({
          title: response.proposal.title,
          proposal: response.proposal.proposal,
          proposed_amount: response.proposal.proposed_amount,
          estimated_timeline: response.proposal.estimated_timeline,
        });
        setAiBidDialogOpen(true);
      } else {
        alert('Failed to generate AI bid');
      }
    } catch (err) {
      console.error('Error generating AI bid:', err);
      const errorData = err.response?.data || err.data || {};
      alert(errorData.error || 'Failed to generate AI bid');
    } finally {
      setGeneratingBid(false);
    }
  };

  const submitAIBid = async () => {
    try {
      setSubmittingBid(true);
      const response = await api.post(`/ai/generate-bid/${id}/`, { auto_submit: true });
      if (response.auto_submitted) {
        setAiBidDialogOpen(false);
        alert('AI-generated bid submitted successfully!');
        checkExistingBid();
        fetchBids();
      }
    } catch (err) {
      const errorData = err.response?.data || err.data || {};
      alert(errorData.error || 'Failed to submit AI bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = { critical: 'error', high: 'warning', medium: 'info', low: 'success' };
    return colors[priority] || 'default';
  };

  const canSubmitBid = () => {
    if (!isAuthenticated) return false;
    if (user?.user_type !== 'supplier') return false;
    if (existingBid) return false;
    if (problem?.status !== 'published') return false;
    return true;
  };

  const isProblemOwner = () => user?.id === problem?.posted_by;

  // ✅ FIXED: Simplified back navigation
  const handleBack = () => {
    navigate('/problems');
  };

  if (!isAuthenticated) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress sx={{ color: rengigsPalette.forestGreen }} />
      </Container>
    );
  }

  if (loading) return (
    <Container sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
      <CircularProgress sx={{ color: rengigsPalette.forestGreen }} />
    </Container>
  );

  if (error) return (
    <Container sx={{ py: 10 }}>
      <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      <Button startIcon={<ArrowBack />} onClick={handleBack}>Back to Problems</Button>
    </Container>
  );

  if (!problem) return (
    <Container sx={{ py: 10 }}>
      <Alert severity="error">Problem not found</Alert>
      <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mt: 2 }}>Back to Problems</Button>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={handleBack}
        sx={{ mb: 3, color: rengigsPalette.charcoal, fontWeight: 600 }}>
        Back to Directory
      </Button>

      {/* AI Suggestions */}
      {showSuggestions && isAuthenticated && (
        <Paper elevation={0} sx={{ mb: 4, overflow: 'hidden', borderRadius: '16px', border: `1px solid ${rengigsPalette.forestGreen}40` }}>
          <Box
            sx={{ p: 2, background: rengigsPalette.gradient, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setSuggestionsExpanded(!suggestionsExpanded)}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Lightbulb sx={{ color: rengigsPalette.brandLime }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>AI Intelligence Insight</Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <IconButton size="small" sx={{ color: 'white' }} onClick={(e) => { e.stopPropagation(); setShowSuggestions(false); }}><Close fontSize="small" /></IconButton>
              <IconButton size="small" sx={{ color: 'white' }}>{suggestionsExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
            </Stack>
          </Box>
          <Collapse in={suggestionsExpanded}>
            <Box sx={{ p: 3, bgcolor: '#FDFFF5' }}>
              <SolutionSuggestions problemId={id} onError={() => setShowSuggestions(false)} />
            </Box>
          </Collapse>
        </Paper>
      )}

      {problem && user?.has_premium_access && (
        <Box sx={{ mb: 3 }}>
          <PricePrediction solutionId={problem.id} onClose={() => {}} />
        </Box>
      )}

      {/* Main Problem Body */}
      <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: '24px', border: `1px solid ${rengigsPalette.border}` }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h3" sx={{ fontWeight: 900, color: rengigsPalette.charcoal, mb: 1, letterSpacing: '-0.5px' }}>
              {problem.title}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={problem.priority} color={getPriorityColor(problem.priority)} size="small" sx={{ fontWeight: 700, textTransform: 'uppercase' }} />
              <Chip label={problem.status} variant="outlined" size="small" sx={{ fontWeight: 700, borderColor: rengigsPalette.border }} />
              <Chip icon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />} label={problem.visibility} variant="outlined" size="small" />
              {problem.is_tender && <Chip label="TENDER" color="warning" size="small" sx={{ fontWeight: 700 }} />}
              <Chip
                icon={<LocalOffer sx={{ fontSize: '14px !important' }} />}
                label={`${problem.solutions_count || 0} solution${(problem.solutions_count || 0) !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  bgcolor: rengigsPalette.softGreen,
                  color: rengigsPalette.forestGreen,
                  fontWeight: 700,
                  border: `1px solid ${rengigsPalette.forestGreen}40`,
                }}
              />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {isProblemOwner() && (
              <Button variant="contained" startIcon={<Edit />} onClick={() => navigate(`/problems/${id}/edit`)}
                sx={{ background: rengigsPalette.charcoal, borderRadius: '12px', px: 3, '&:hover': { background: '#000' } }}>
                Edit Details
              </Button>
            )}
            {isAuthenticated && (
              <Button variant="contained" startIcon={<LocalOffer />} onClick={openTagDialog}
                sx={{ background: rengigsPalette.gradient, borderRadius: '12px', px: 3, textTransform: 'none', fontWeight: 700 }}>
                Tag Solution
              </Button>
            )}
          </Stack>
        </Box>

        {/* Poster + Bid */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, p: 2.5, bgcolor: rengigsPalette.softBg, borderRadius: '16px', border: `1px solid ${rengigsPalette.forestGreen}15`, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar src={problem.organization_detail?.logo} sx={{ width: 56, height: 56, mr: 2, border: `2px solid ${rengigsPalette.brandLime}` }}>
              {problem.organization_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.deepForest }}>{problem.organization_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Problem ID: #{id.slice(0,8)} · {formatDistance(new Date(problem.created_at), new Date(), { addSuffix: true })}
              </Typography>
            </Box>
          </Box>
          {canSubmitBid() && (
            <Button variant="contained" startIcon={<Gavel />} onClick={() => setBidDialogOpen(true)}
              sx={{ background: rengigsPalette.gradient, borderRadius: '24px', px: 4, py: 1, textTransform: 'none', fontWeight: 700 }}>
              Submit Bid / EOI
            </Button>
          )}
          {existingBid && <Chip icon={<CheckCircle sx={{ fontSize: 16 }} />} label="Bid Submitted" color="success" sx={{ fontWeight: 600 }} />}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={5}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <div style={{ width: 4, height: 24, background: rengigsPalette.forestGreen, borderRadius: 2 }} />
              The Challenge
            </Typography>
            <Typography paragraph sx={{ lineHeight: 1.8, color: '#374151', fontSize: '1.05rem' }}>
              {problem.description}
            </Typography>

            {problem.background && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2, color: rengigsPalette.deepForest }}>Contextual Background</Typography>
                <Typography paragraph sx={{ lineHeight: 1.8, color: '#4B5563', bgcolor: '#F9FAFB', p: 3, borderRadius: '12px', borderLeft: `4px solid ${rengigsPalette.brandLime}` }}>
                  {problem.background}
                </Typography>
              </Box>
            )}

            {/* Tagged Solutions Section */}
            <Box sx={{ mt: 5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOffer sx={{ color: rengigsPalette.forestGreen, fontSize: 22 }} />
                  Tagged Solutions
                  <Chip label={taggedSolutions.length} size="small" sx={{ bgcolor: rengigsPalette.softGreen, color: rengigsPalette.forestGreen, fontWeight: 800, ml: 1 }} />
                </Typography>
                {isAuthenticated && (
                  <Button variant="outlined" size="small" startIcon={<LocalOffer />} onClick={openTagDialog}
                    sx={{ borderColor: rengigsPalette.forestGreen, color: rengigsPalette.forestGreen, borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}>
                    Tag a Solution
                  </Button>
                )}
              </Box>

              {loadingTagged ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={32} sx={{ color: rengigsPalette.forestGreen }} />
                </Box>
              ) : taggedSolutions.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', border: `2px dashed ${rengigsPalette.border}`, borderRadius: '16px' }}>
                  <LocalOffer sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                  <Typography color="text.secondary">No solutions tagged yet.</Typography>
                  {isAuthenticated && (
                    <Button variant="text" onClick={openTagDialog}
                      sx={{ color: rengigsPalette.forestGreen, fontWeight: 700, mt: 1, textTransform: 'none' }}>
                      Be the first to tag a solution
                    </Button>
                  )}
                </Box>
              ) : (
                <Stack spacing={2}>
                  {taggedSolutions.map((tag) => (
                    <Card key={tag.tag_id} elevation={0} sx={{
                      borderRadius: '16px',
                      border: `1px solid ${rengigsPalette.border}`,
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: rengigsPalette.mediumGreen, boxShadow: `0 4px 16px ${rengigsPalette.forestGreen}15` }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              {tag.logo ? (
                                <Avatar src={tag.logo} sx={{ width: 28, height: 28 }} />
                              ) : (
                                <Avatar sx={{ width: 28, height: 28, bgcolor: rengigsPalette.softGreen, color: rengigsPalette.forestGreen, fontSize: '0.8rem', fontWeight: 800 }}>
                                  {tag.title?.[0]}
                                </Avatar>
                              )}
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 800, color: rengigsPalette.forestGreen, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                onClick={() => navigate(`/solutions/${tag.solution_id}`)}
                              >
                                {tag.title}
                              </Typography>
                              {tag.is_verified && (
                                <Tooltip title="Verified solution">
                                  <CheckCircle sx={{ fontSize: 16, color: rengigsPalette.forestGreen }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              <Chip label={tag.solution_type} size="small" sx={{ bgcolor: '#E1ECF4', color: '#39739D', borderRadius: '4px', fontSize: '0.7rem' }} />
                              <Chip label={tag.provider_name} size="small" variant="outlined" sx={{ borderRadius: '4px', fontSize: '0.7rem', borderColor: rengigsPalette.border }} />
                              {tag.price && (
                                <Chip label={`${tag.currency} ${tag.price}`} size="small"
                                  sx={{ bgcolor: rengigsPalette.softGreen, color: rengigsPalette.forestGreen, borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700 }} />
                              )}
                              {tag.rating_avg > 0 && (
                                <Chip label={`★ ${Number(tag.rating_avg).toFixed(1)}`} size="small"
                                  sx={{ bgcolor: '#FFF9E6', color: '#92600A', borderRadius: '4px', fontSize: '0.7rem' }} />
                              )}
                            </Stack>
                          </Box>

                          <Stack direction="row" spacing={1} sx={{ ml: 2, flexShrink: 0 }}>
                            <Tooltip title="View solution detail">
                              <IconButton size="small" onClick={() => navigate(`/solutions/${tag.solution_id}`)}>
                                <OpenInNew sx={{ fontSize: 18, color: rengigsPalette.forestGreen }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Contact provider">
                              <IconButton size="small" onClick={() => navigate(`/solutions/${tag.solution_id}?contact=1`)}>
                                <ContactMail sx={{ fontSize: 18, color: rengigsPalette.forestGreen }} />
                              </IconButton>
                            </Tooltip>
                            {tag.can_untag && (
                              <Tooltip title="Remove tag">
                                <IconButton size="small" onClick={() => handleUntagSolution(tag.solution_id)}>
                                  <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>

                        {tag.tagline && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {tag.tagline}
                          </Typography>
                        )}

                        {tag.note && (
                          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: rengigsPalette.softBg, borderRadius: '8px', borderLeft: `3px solid ${rengigsPalette.brandLime}` }}>
                            <Typography variant="caption" sx={{ color: '#5F6D7C', fontStyle: 'italic' }}>
                              "{tag.note}"
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            Tagged by {tag.tagged_by_email} · {formatDistance(new Date(tag.created_at), new Date(), { addSuffix: true })}
                          </Typography>
                          <Button
                            size="small" variant="contained"
                            onClick={() => navigate(`/solutions/${tag.solution_id}?contact=1`)}
                            sx={{ background: rengigsPalette.gradient, borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem' }}
                          >
                            Contact Provider
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Bids list for org */}
            {user?.user_type === 'organization' && bidsList.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Button variant="text" startIcon={<Gavel />} onClick={() => setShowBidsList(!showBidsList)} sx={{ color: rengigsPalette.forestGreen }}>
                  {showBidsList ? 'Hide' : 'View'} Bids ({bidsList.length})
                </Button>
                {showBidsList && (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    {bidsList.map((bid) => (
                      <Card key={bid.id} variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{bid.title}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{bid.proposal.substring(0, 200)}...</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {bid.proposed_amount ? `${bid.proposed_currency} ${bid.proposed_amount}` : 'Amount TBD'}
                            </Typography>
                            <Chip label={bid.status} size="small" color={bid.status === 'accepted' ? 'success' : 'default'} />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={3} sx={{
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            borderLeft: { md: `1px solid ${rengigsPalette.border}` }, pl: { md: 4 },
          }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon fontSize="small" /> Views
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{problem.views_count || 0}</Typography>
              </Box>
              <Box onClick={e => { e.stopPropagation(); navigate(`/problems/${problem.id}`); }}
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                <Typography variant="body2" sx={{ color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ChatBubbleOutline fontSize="small" /> Solutions
                </Typography>
                <Chip label={problem.solutions_count || 0} size="small"
                  sx={{
                    bgcolor: problem.solutions_count > 0 ? rengigsPalette.softGreen : 'transparent',
                    color: problem.solutions_count > 0 ? rengigsPalette.forestGreen : '#94A3B8',
                    fontWeight: 800,
                    border: `1px solid ${problem.solutions_count > 0 ? rengigsPalette.forestGreen : '#E1E9F0'}`,
                  }} />
              </Box>
              <Button variant="outlined" fullWidth
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, borderColor: rengigsPalette.forestGreen, color: rengigsPalette.forestGreen, '&:hover': { background: rengigsPalette.gradient, color: 'white', border: 'none' } }}>
                View Details
              </Button>
              <Button variant="contained" fullWidth
                startIcon={<LocalOffer sx={{ fontSize: 16 }} />}
                onClick={e => openTagDialog(e, problem)}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, background: rengigsPalette.gradient, '&:hover': { background: rengigsPalette.gradientReverse } }}>
                Tag Solution
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Activity Feed */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 4 }}>
            Activity Feed <Chip label={problem.updates?.length || 0} size="small" sx={{ ml: 1, bgcolor: rengigsPalette.charcoal, color: 'white' }} />
          </Typography>
          {isAuthenticated && (
            <Paper elevation={0} sx={{ p: 3, mb: 5, borderRadius: '16px', border: `1px solid ${rengigsPalette.border}`, bgcolor: '#F9FAFB' }}>
              <Stack direction="row" spacing={2}>
                <Avatar sx={{ bgcolor: rengigsPalette.forestGreen, fontWeight: 700 }}>{user?.full_name?.[0]}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <TextField fullWidth multiline rows={2} placeholder="Share an update or ask a question..."
                    value={comment} onChange={(e) => setComment(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' }, mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" endIcon={<Send />} onClick={handleAddUpdate}
                      disabled={submitting || !comment.trim()}
                      sx={{ background: rengigsPalette.gradient, borderRadius: '10px', px: 4 }}>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Post Update'}
                    </Button>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          )}
          <Stack spacing={2}>
            {problem.updates?.length > 0 ? problem.updates.map((update) => (
              <Card key={update.id} elevation={0} sx={{ borderRadius: '16px', border: `1px solid ${rengigsPalette.border}` }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={2} mb={2}>
                    <Avatar sx={{ bgcolor: rengigsPalette.brandLime, color: rengigsPalette.charcoal, fontWeight: 800 }}>{update.user_full_name?.[0]}</Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{update.user_full_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistance(new Date(update.created_at), new Date(), { addSuffix: true })}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ color: '#374151', lineHeight: 1.6 }}>{update.content}</Typography>
                </CardContent>
              </Card>
            )) : (
              <Box sx={{ py: 6, textAlign: 'center', border: `2px dashed ${rengigsPalette.border}`, borderRadius: '20px' }}>
                <Typography color="text.secondary">No activity yet. Start the conversation!</Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Paper>

      {/* Tag Solution Dialog */}
      <Dialog open={tagDialogOpen} onClose={() => setTagDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ background: rengigsPalette.gradient, color: 'white', fontWeight: 800 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocalOffer /> Tag a Solution to this Problem</Box>
            <IconButton size="small" onClick={() => setTagDialogOpen(false)} sx={{ color: 'white' }}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {tagSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircle sx={{ fontSize: 56, color: rengigsPalette.forestGreen, mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: rengigsPalette.charcoal, mb: 1 }}>Solution Tagged!</Typography>
              <Typography color="text.secondary"><strong>{tagSuccess}</strong> has been tagged to this problem.</Typography>
              <Button variant="contained" sx={{ mt: 3, background: rengigsPalette.gradient, borderRadius: '10px', textTransform: 'none' }}
                onClick={() => { setTagDialogOpen(false); setTagSuccess(null); }}>Done</Button>
            </Box>
          ) : (
            <>
              {loadingSolutions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: rengigsPalette.forestGreen }} /></Box>
              ) : (
                <>
                  <Autocomplete
                    options={mySolutions}
                    getOptionLabel={(opt) => opt.title || ''}
                    value={selectedSolution}
                    onChange={(e, val) => setSelectedSolution(val)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{option.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.solution_type} · {option.provider_name}</Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label="Select your solution *" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                    )}
                    sx={{ mb: 3 }}
                    noOptionsText="No solutions found. Create one at /solutions/create"
                  />
                  <TextField fullWidth multiline rows={3}
                    label="Why does this solution fit? (optional)"
                    placeholder="Briefly explain how your solution addresses this problem..."
                    value={tagNote} onChange={(e) => setTagNote(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                  {mySolutions.length === 0 && !loadingSolutions && (
                    <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                      No solutions found.{' '}
                      <Button size="small" onClick={() => navigate('/solutions/create')}
                        sx={{ color: rengigsPalette.forestGreen, p: 0, textTransform: 'none', fontWeight: 700 }}>
                        Create one first
                      </Button>
                    </Alert>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        {!tagSuccess && (
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={() => setTagDialogOpen(false)} sx={{ color: rengigsPalette.charcoal }}>Cancel</Button>
            <Button variant="contained" onClick={handleTagSolution}
              disabled={!selectedSolution || tagging}
              startIcon={tagging ? <CircularProgress size={18} color="inherit" /> : <LocalOffer />}
              sx={{ background: rengigsPalette.gradient, borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 4 }}>
              {tagging ? 'Tagging...' : 'Tag Solution'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Already Tagged Dialog */}
      <Dialog
        open={alreadyTaggedDialogOpen}
        onClose={() => setAlreadyTaggedDialogOpen(false)}
        TransitionComponent={SlideTransition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: rengigsPalette.gradient,
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(46,125,50,0.3), 0 4px 12px rgba(0,0,0,0.1)',
            m: 2,
          }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            {/* Warning Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
                animation: 'pulse 0.5s ease-in-out',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.8)', opacity: 0 },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }}
            >
              <ErrorOutline sx={{ fontSize: 48, color: rengigsPalette.white }} />
            </Box>

            {/* Title */}
            <Typography variant="h5" sx={{ fontWeight: 700, color: rengigsPalette.white, mb: 1 }}>
              Already Tagged
            </Typography>
            
            {/* Solution Name */}
            <Typography variant="body1" sx={{ color: rengigsPalette.white, opacity: 0.9, mb: 1, fontWeight: 500 }}>
              {alreadyTaggedSolution?.title}
            </Typography>
            
            {/* Message */}
            <Typography variant="body2" sx={{ color: rengigsPalette.white, opacity: 0.8, mb: 2 }}>
              This solution has already been tagged to this problem
            </Typography>

            {/* Progress indicator for auto-close */}
            <Box sx={{ width: '100%', mb: 3 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '4px',
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    background: rengigsPalette.white,
                    borderRadius: '2px',
                    animation: 'shrink 4s linear forwards',
                    '@keyframes shrink': {
                      '0%': { width: '100%' },
                      '100%': { width: '0%' },
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Single Action Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => setAlreadyTaggedDialogOpen(false)}
              sx={{
                backgroundColor: rengigsPalette.white,
                color: rengigsPalette.forestGreen,
                py: 1.2,
                borderRadius: '10px',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: rengigsPalette.white,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              Got it
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Bid Dialog */}
      <Dialog open={bidDialogOpen} onClose={() => setBidDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: rengigsPalette.gradient, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Gavel /> Submit Bid / Expression of Interest</Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Info fontSize="small" />
              <Typography variant="body2">Use our AI Bid Generator to create a professional proposal instantly!</Typography>
              <Button size="small" startIcon={<AutoAwesome />} onClick={generateAIBid} disabled={generatingBid}
                sx={{ ml: 'auto', color: rengigsPalette.forestGreen }}>
                {generatingBid ? 'Generating...' : 'Generate with AI'}
              </Button>
            </Stack>
          </Alert>
          <TextField fullWidth label="Bid Title *" value={bidData.title}
            onChange={(e) => setBidData({ ...bidData, title: e.target.value })} margin="normal" sx={{ mb: 2 }} />
          <TextField fullWidth multiline rows={6} label="Proposal / Solution Description *"
            value={bidData.proposal} onChange={(e) => setBidData({ ...bidData, proposal: e.target.value })} margin="normal" sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth type="number" label="Proposed Amount (Optional)" value={bidData.proposed_amount}
                onChange={(e) => setBidData({ ...bidData, proposed_amount: e.target.value })}
                InputProps={{ startAdornment: <span>$</span> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Estimated Timeline" placeholder="e.g., 2-3 weeks, 1 month"
                value={bidData.estimated_timeline} onChange={(e) => setBidData({ ...bidData, estimated_timeline: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setBidDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBidSubmit}
            disabled={submittingBid || !bidData.title || !bidData.proposal}
            sx={{ background: rengigsPalette.gradient }}>
            {submittingBid ? <CircularProgress size={24} /> : 'Submit Bid'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Bid Preview Dialog */}
      <Dialog open={aiBidDialogOpen} onClose={() => setAiBidDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ background: rengigsPalette.gradient, color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AutoAwesome /> AI-Generated Bid Proposal</Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {aiBidProposal && (
            <>
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                AI Confidence Score: {aiBidProposal.confidence_score}%
                {aiBidProposal.confidence_score > 80 && ' - Excellent match!'}
              </Alert>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Title</Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>{aiBidProposal.title}</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Proposal</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                <Typography variant="body2">{aiBidProposal.proposal}</Typography>
              </Paper>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Proposed Amount</Typography>
                  <Typography variant="h6" sx={{ color: rengigsPalette.forestGreen }}>${aiBidProposal.proposed_amount}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Estimated Timeline</Typography>
                  <Typography variant="h6">{aiBidProposal.estimated_timeline}</Typography>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setAiBidDialogOpen(false)}>Edit Manually</Button>
          <Button variant="contained" onClick={submitAIBid} disabled={submittingBid}
            sx={{ background: rengigsPalette.gradient }}>
            {submittingBid ? <CircularProgress size={24} /> : 'Submit AI Bid'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProblemDetailPage;